'use client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import * as Sentry from '@sentry/nextjs'
import { GraphQLFormattedError } from 'graphql'
import { get } from 'local-storage'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { v4 as uuidv4 } from 'uuid'

import { useHasRoles } from '../hooks/useHasRoles'
import { SWITCH_BUILDER } from '../modules/adminUsers/graphQL'
import { Role } from '../modules/adminUsers/roles'
import { getUserName } from '../utils'

import useAnalytics from './analytics'
import { useAuth } from './authContext'
import { LS_SUPPORT_KEY } from './supportContext'
import { useThemeContext } from './themeContext'
import { useWhiteLabelling } from './whiteLabelContext'

import { ErrorLayout } from '@/components/layout-components/ErrorLayout'
import { Alert } from '@/components/shared-components/BUI/Alert'
import { Button } from '@/components/shared-components/BUI/Button'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { openLiveChatToNewConversation } from '@/components/util-components/live-chat/LiveChat'
import { useProjectFetch } from '@/hooks/useProjectFetch'
import {
  GET_LOGGED_IN_USER,
  GET_LOGGED_IN_USER_SETTINGS,
} from '@/modules/user/graphQL'

import {
  LoggedInUserProjectsUiQuery,
  LoggedInUserSettingsUiQuery,
  LoggedInUserSettingsUiQueryVariables,
  LoggedInUserUiQuery,
  LoggedInUserUiQueryVariables,
  ProjectStatus,
  SwitchBuilderUiMutation,
  SwitchBuilderUiMutationVariables,
} from 'types/api.graphql'

export type User = LoggedInUserUiQuery['user']
export type UsersAdminUser = User['adminUser']
export type UsersBuilder = UsersAdminUser['builder']
export type UsersProjects = LoggedInUserProjectsUiQuery['projectsForBuilder']

type UserContextValue = {
  userLoading: boolean
  projectsLoading: boolean
  refetchUser: () => void
  refetchProjects: () => void
  user: User
  adminUser: Omit<UsersAdminUser, 'builder'>
  builder: Omit<UsersBuilder, 'projects'>
  builders: LoggedInUserUiQuery['user']['adminUser']['builders'][0]['builder'][]
  projects: UsersProjects
  dashboards: LoggedInUserUiQuery['user']['adminUser']['dashboards']
  integrations: LoggedInUserUiQuery['user']['adminUser']['builder']['integrations']
  checklistCategories?: (LoggedInUserSettingsUiQuery['checklistSettings']['checklistCategories'][number] & {
    isInNavigation: boolean
  })[]
  aiTools: LoggedInUserSettingsUiQuery['aiToolsSettings']
  settings: LoggedInUserSettingsUiQuery['userSettings']
  gallerySettings: LoggedInUserSettingsUiQuery['gallerySettings']
  permissions: LoggedInUserUiQuery['user']['adminUser']['roles'][0]['permissions']
}

const User = createContext<UserContextValue>(null)

const getBuilderFromLocalStorage = () => get<{ id: string }>(LS_SUPPORT_KEY)

// Use to use this to ignore helpscout, may use this to ignore intercom
const IGNOREABLE_ERRORS = []
const ACTIVE_PROJECT_STATUSES = [
  ProjectStatus.InProgress,
  ProjectStatus.Upcoming,
]
const COMPLETED_PROJECT_STATUSES = [ProjectStatus.Complete]
const DD_REQUEST_KEY = 'x-buildpass-request-id'

const safeToIgnoreErrors = (errors: ReadonlyArray<GraphQLFormattedError>) => {
  if (!errors?.length) return false
  return errors.every(
    (e) => e.path && IGNOREABLE_ERRORS.includes(`${e.path[0]}`)
  )
}

export const UserProvider = ({ children }) => {
  const isSuperAdmin = useHasRoles(Role.SuperAdmin)

  const { push } = useRouter()
  const { identifyUser } = useAnalytics()
  const { setThemeColours } = useThemeContext()
  const { setWhiteLabelConfig } = useWhiteLabelling()
  const { isLoaded, isSignedIn, orgId } = useAuth()
  const { roles } = useAuth()

  const storedBuilder = useMemo(getBuilderFromLocalStorage, [])

  const skip = !isLoaded || !isSignedIn || !orgId

  // Be careful when skip is true, loading can be false and we have no data
  const {
    data,
    loading: userLoading,
    refetch: refetchUser,
    error,
  } = useQuery<LoggedInUserUiQuery, LoggedInUserUiQueryVariables>(
    GET_LOGGED_IN_USER,
    {
      skip,
      // We get back both errors and data and handle as needed
      errorPolicy: 'all',
      onCompleted: (data) => {
        const user = data?.user

        if (user?.id) {
          const { id, email, createdAt, firstName, lastName } = user
          const name = getUserName(user)

          Sentry.setUser({ id: data.user?.id })
          datadogRum.setUser({ id, email, name })
          datadogLogs.setUser({ id, email, name })
          datadogLogs.setGlobalContextProperty(DD_REQUEST_KEY, uuidv4())

          identifyUser({
            user: { id, email, createdAt, firstName, lastName },
            builder: {
              subscription: user?.adminUser?.builder?.subscription,
              company: user?.adminUser?.builder?.company,
              id: user?.adminUser?.builder?.id,
            },
            userRoles: roles,
            modules: user?.adminUser?.modules,
          })
        }

        setWhiteLabelConfig(data?.user?.adminUser?.builder)
      },
      onError: (error) => {
        if (error?.message === 'Your account has been deactivated') {
          push('/deactivated')
        } else {
          Sentry.captureException(error, {
            extra: {
              message: 'Error fetching logged in user',
              data,
            },
          })
        }
      },
    }
  )

  const {
    data: settingsData,
    loading: settingsLoading,
    refetch: refetchSettings,
  } = useQuery<
    LoggedInUserSettingsUiQuery,
    LoggedInUserSettingsUiQueryVariables
  >(GET_LOGGED_IN_USER_SETTINGS, {
    skip,
    onCompleted: (data) => {
      setThemeColours(data?.stylingSettings)
    },
  })

  const {
    projects: activeProjects,
    fetchedAllProjects: fetchedAllActiveProjects,
    refetchProjects: refetchActiveProjects,
  } = useProjectFetch(ACTIVE_PROJECT_STATUSES, skip)

  const {
    projects: completedProjects,
    fetchedAllProjects: fetchedAllCompletedProjects,
    refetchProjects: refetchCompletedProjects,
  } = useProjectFetch(
    COMPLETED_PROJECT_STATUSES,
    skip || !fetchedAllActiveProjects // wait for all active projects to be fetched first
  )

  const [switchBuilder, { loading: switchBuilderLoading }] = useMutation<
    SwitchBuilderUiMutation,
    SwitchBuilderUiMutationVariables
  >(SWITCH_BUILDER, {
    variables: { orgId },
    refetchQueries: [
      {
        query: GET_LOGGED_IN_USER,
        fetchPolicy: 'network-only',
      },
    ],
  })

  const projects = useMemo(() => {
    return activeProjects.concat(completedProjects)
  }, [activeProjects, completedProjects])

  const builder = useMemo(
    () => data?.user?.adminUser?.builder ?? ({} as UserContextValue['builder']),
    [data?.user]
  )

  const adminUser = useMemo(() => {
    const { builder, ...adminUser } = data?.user?.adminUser ?? {} // eslint-disable-line @typescript-eslint/no-unused-vars
    return adminUser as UserContextValue['adminUser']
  }, [data?.user?.adminUser])

  const checklistCategories = useMemo(() => {
    return (
      settingsData?.checklistSettings?.checklistCategories?.map((c) => ({
        ...c,
        isInNavigation:
          settingsData?.checklistSettings?.checklistCategoriesInNavigation?.includes(
            c.id
          ),
      })) ?? []
    )
  }, [settingsData?.checklistSettings])

  const projectsLoading = useMemo(() => {
    return !fetchedAllActiveProjects || !fetchedAllCompletedProjects
  }, [fetchedAllActiveProjects, fetchedAllCompletedProjects])

  const refetchUserAndSettings = useCallback(() => {
    refetchUser()
    refetchSettings()
  }, [refetchSettings, refetchUser])

  const refetchProjects = useCallback(() => {
    refetchActiveProjects()
    refetchCompletedProjects()
  }, [refetchActiveProjects, refetchCompletedProjects])

  useEffect(() => {
    if (isSuperAdmin) return // super admins need to be able to impersonate other builders

    if (isLoaded && !orgId) {
      push('/select-organisation')
      return
    }

    if (!isLoaded || userLoading || skip) return

    if (orgId && builder?.clerkOrganizationId !== orgId) {
      switchBuilder() // need to fetch the user and all other data because it was fetched for the wrong builder
    }
  }, [
    isLoaded,
    switchBuilder,
    userLoading,
    skip,
    orgId,
    isSuperAdmin,
    storedBuilder,
    builder?.id,
    push,
    builder?.clerkOrganizationId,
  ])

  if (orgId && !userLoading && !data?.user && error) {
    if (!safeToIgnoreErrors(error.graphQLErrors)) {
      return (
        <ErrorLayout title="Error loading your account">
          We&apos;re very sorry about this but we cannot load your BuildPass
          user account.
          <br />
          Please speak to one of our helpful staff to get this sorted, or try
          log in again.
          <Button onClick={openLiveChatToNewConversation}>
            Contact BuildPass Support
          </Button>
          <Link href="/logout">
            <Button>Log out</Button>
          </Link>
          {process.env.NODE_ENV === 'development' && (
            <Flexbox justifyContent="center" mt="4rem">
              <Alert
                variant="text"
                title="Psst, hey there devacino. Make sure your API is actually running..."
              />
            </Flexbox>
          )}
        </ErrorLayout>
      )
    }
  }

  return (
    <User.Provider
      value={{
        user: data?.user,
        adminUser,
        builder,
        builders: data?.user?.adminUser?.builders.map((b) => b.builder),
        integrations: data?.user?.adminUser?.builder?.integrations,
        projects,
        permissions: data?.user?.adminUser?.roles?.flatMap(
          (r) => r.permissions
        ),
        dashboards: data?.user?.adminUser?.dashboards,
        userLoading:
          userLoading ||
          settingsLoading ||
          skip ||
          switchBuilderLoading ||
          !isLoaded ||
          (isLoaded && !orgId),

        projectsLoading,
        checklistCategories,
        aiTools: settingsData?.aiToolsSettings,
        settings: settingsData?.userSettings,
        gallerySettings: settingsData?.gallerySettings,
        refetchUser: refetchUserAndSettings,
        refetchProjects,
      }}
    >
      {children}
    </User.Provider>
  )
}

export const useUser = () => useContext(User)
